// Pre-evaluation type-check pass (§5.8).
//
// Walks a normalised AST top-down, builds a schema env from `(dataset
// name <expr>)` bindings, and propagates Schemas through every
// `(transform ds verb1 verb2 …)` pipeline via each verb's
// `propagateSchema`. Along the way it validates expression-context
// sub-trees (filter predicates, derive expressions) and column-list
// args (select, distinct, rename) for unknown column references.
//
// All detected diagnostics are returned as a flat list — never thrown.
// The caller (`evalScript`) wraps a non-empty list in
// `AggregateExprError` and throws, so all type errors surface in one
// LLM round-trip (§3.7, §9.3).
//
// Phase 1.5 scope (minimum viable):
//   - `(dataset name <Dataset-literal>)`           → bind in schemaEnv
//   - `(dataset name <expr>)` w/ non-Dataset RHS   → skip; schema unknown
//   - `(transform <ds-ref> verb1 verb2 …)`         → walk + propagate
//   - Per-verb validation is dispatched by name on a small table here.
//     Extension verbs added later (aggregate/join/…) register their
//     own validators when they land.

import { colNodeLabels, pickColumnPath } from '../column-ref.ts';
import { type ExprError, UnknownColumnRefError } from '../errors.ts';
import { type AstInput, astShape, isOpCall, nodePos, type OpCall, type Spans } from '../normalize.ts';
import { type ColumnRefInArg, type NameTable, pipelineVerbs, type ValidateArgsCtx, type VerbMeta } from '../op-meta.ts';
import { elemPath, paramPath } from '../path.ts';
import { Dataset } from '../value.ts';
import { runPropagateSchema } from './infer.ts';
import type { Schema } from './types.ts';

/** Aggregated environment for type-check: dataset-name → its Schema. */
type SchemaEnv = Map<string, Schema>;

/**
 * Run the pre-evaluation type-check pass against the given script.
 * Returns a flat list of diagnostics. An empty list means "no
 * statically-detectable type errors" — the script may still fail at
 * runtime for reasons the pass can't see (missing I/O dependencies,
 * arquero-level errors).
 */
export const typeCheckScript = (
  script: readonly AstInput[],
  ops: NameTable,
  verbs: NameTable,
  spans: Spans,
): readonly ExprError[] => {
  const errors: ExprError[] = [];
  const schemaEnv: SchemaEnv = new Map();

  script.forEach((form, i) => {
    const path = elemPath('', i);
    if (!isOpCall(form)) {
      return;
    }
    if (form.name === 'dataset') {
      handleDatasetForm(form, schemaEnv);
      return;
    }
    if (form.name === 'transform') {
      handleTransformForm(form, path, schemaEnv, ops, verbs, spans, errors);
      return;
    }
    return;
    // Other top-level forms: no schema-level analysis in this phase.
  });

  return errors;
};

/**
 * `(dataset name <expr>)` — bind `name → schema` in `schemaEnv` when
 * the RHS is a known schema source (currently: a JS-object Dataset
 * literal). Other RHS shapes are silently skipped; the binding's
 * schema simply isn't tracked, and downstream `(transform name …)`
 * uses fall back to "unknown input schema" → no diagnostics emitted.
 */
const handleDatasetForm = (form: OpCall, schemaEnv: SchemaEnv): void => {
  const params = form.params ?? [];
  const [nameArg, rhsArg] = params;
  if (!isSymbolRef(nameArg)) {
    return;
  }
  if (rhsArg instanceof Dataset) {
    schemaEnv.set(nameArg.$sym, rhsArg.schema);
  }
};

/**
 * `(transform <ds-ref> verb1 verb2 …)` — resolve the input schema,
 * then for each verb run its `referencedColumns` (default unknown-ref
 * diagnostics) and `validateArgs` (custom diagnostics) against the
 * running schema, and advance the schema via the verb's
 * `propagateSchema` (via `runPropagateSchema`).
 *
 * Every verb-meta hook is wrapped in `safeCall` — the pre-eval pass is
 * contractually total: one verb's misbehaving hook must not poison
 * diagnostics for later verbs or other forms in the same script.
 */
const handleTransformForm = (
  form: OpCall,
  path: string,
  schemaEnv: SchemaEnv,
  ops: NameTable,
  verbs: NameTable,
  spans: Spans,
  errors: ExprError[],
): void => {
  const params = form.params ?? [];
  const [dsArg, ...verbAsts] = params;
  let schema = resolveDsArg(dsArg, schemaEnv);
  if (schema === undefined) {
    return; // unknown input schema — defer to runtime
  }
  verbAsts.forEach((verbAst, i) => {
    // Capture schema at iteration start. `schema` is re-assignable
    // across iterations, but within one iteration we want a const-
    // narrowed view so TS can track non-undefinedness through the
    // nested ctx-closure.
    const inputSchema = schema;
    if (inputSchema === undefined) {
      return; // schema tracking lost from a prior verb; skip this one
    }
    const verbPath = paramPath(path, i + 1);
    if (!isOpCall(verbAst)) {
      return;
    }
    const verbMeta = resolveVerbMeta(verbAst.name, verbs);
    if (verbMeta === undefined) {
      return; // unknown verb — surfaced at runtime via UnknownVerbError
    }
    // Default unknown-ref diagnostics: derive refs from the verb's
    // `referencedColumns` hook (SSOT shared with the runtime dispatcher's
    // per-cell axis detection) and validate each name against the
    // running input schema.
    if (verbMeta.referencedColumns !== undefined) {
      const refs = safeCall(
        () => verbMeta.referencedColumns?.(verbAst as never, verbPath) as readonly ColumnRefInArg[],
        [] as readonly ColumnRefInArg[],
      );
      errors.push(...validateRefs(refs, inputSchema, spans));
    }
    // Verb-custom validation. Reserved for diagnostics beyond plain
    // unknown-ref checks (none of the current builtins use it; the hook
    // remains for future verbs whose arg validation can't be reduced to
    // a column-ref list).
    if (verbMeta.validateArgs !== undefined) {
      const validateCtx: ValidateArgsCtx = { inputSchema, ops, spans, path: verbPath };
      const verbErrors = safeCall(
        () => verbMeta.validateArgs?.(verbAst as never, validateCtx) as readonly ExprError[],
        [] as readonly ExprError[],
      );
      errors.push(...verbErrors);
    }
    // Advance the running schema via propagateSchema. If the verb has
    // no propagator the helper returns `undefined`; if its propagator
    // throws we fall back to `undefined`, dropping schema tracking for
    // later verbs in this pipeline.
    schema = safeCall(() => runPropagateSchema(verbMeta, verbAst, inputSchema, ops, spans, verbPath), undefined);
  });
};

/**
 * Resolve a transform's first positional arg (`<ds-ref>`) to a Schema.
 * Accepts a symbol ref against schemaEnv or a JS-object Dataset
 * literal. Anything else (transform of a transform, etc.) returns
 * undefined — the pass skips analysis for that pipeline.
 */
const resolveDsArg = (arg: unknown, schemaEnv: SchemaEnv): Schema | undefined => {
  if (arg instanceof Dataset) {
    return arg.schema;
  }
  if (isSymbolRef(arg)) {
    return schemaEnv.get(arg.$sym);
  }
  return undefined;
};

const resolveVerbMeta = (name: string, verbs: NameTable): VerbMeta | undefined => {
  const schema = verbs.get(name);
  if (schema === undefined) {
    return undefined;
  }
  // `z.registry.get`'s return shape is the `$output`-substituted variant
  // of `VerbMeta` (apply's data becomes `Record<string, unknown>`, etc.),
  // which structurally differs from the unmaterialised `VerbMeta` union.
  // The cast is the same pattern the dispatcher uses in `evaluator.ts`.
  return pipelineVerbs.get(schema) as VerbMeta | undefined;
};
/**
 * Run `fn`; on throw return `fallback`. Used by `handleTransformForm` to
 * keep the pre-eval type-check pass total — a misbehaving verb-meta
 * hook (`referencedColumns`, `validateArgs`, `propagateSchema`) must
 * not poison diagnostics for later verbs or other forms in the same
 * script. The error itself is discarded; the diagnostic surface is
 * intentionally type-check-only (runtime catches the same throw via
 * `dispatchCall`'s `OpExecutionError` wrap).
 */
const safeCall = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

const isSymbolRef = (node: unknown): node is { readonly $sym: string } => {
  return astShape(node).kind === 'symbolRef';
};

/**
 * Walk an expression-context AST and collect every `symbolRef` as a
 * `ColumnRefInArg` (name + spans-path pair). Mirror of `inferExprType`'s
 * shape walk; this one stays free of type inference and only enumerates
 * refs.
 *
 * Verbs use this from their `referencedColumns` meta hook on expression
 * args (filter predicates, derive expressions); the engine then runs
 * `validateRefs` to turn the list into unknown-column-ref diagnostics
 * and inspects the same list for per-cell dispatch detection.
 */
export const exprColRefs = (node: unknown, basePath: string): readonly ColumnRefInArg[] => {
  const refs: ColumnRefInArg[] = [];
  const visit = (n: unknown, p: string): void => {
    const shape = astShape(n);
    switch (shape.kind) {
      case 'symbolRef':
        refs.push({ name: shape.sym, path: p });
        return;
      case 'vector':
        shape.elements.forEach((el, i) => {
          visit(el, elemPath(p, i));
        });
        return;
      case 'opCall':
        // `(col …)` params are name LABELS (binding / column / struct
        // sub-path), not sub-expressions — recursing would mis-report
        // the sub-path segments (and a leading binding name) as missing
        // top-level columns. Emit ONE `colLabels` ref carrying the raw
        // tuple; `validateRefs` resolves it schema-aware via the shared
        // role-based `pickColumnPath` (valid iff *some* label is a
        // column), so a genuinely typo'd column still surfaces a
        // pre-I/O `UnknownColumnRefError`. `colNodeLabels` returns
        // `undefined` for a non-literal label set (a Form-2 `(col
        // (expr) …)`); that case can't be resolved statically, so it
        // is left for the runtime guard in `(col …)`'s `compileExpr`.
        if (shape.call.name === 'col') {
          const labels = colNodeLabels(n);
          if (labels !== undefined && labels.length > 0) {
            refs.push({ name: `(col ${labels.join(' ')})`, path: p, colLabels: labels });
          }
          return;
        }
        (shape.call.params ?? []).forEach((arg, i) => {
          visit(arg, paramPath(p, i));
        });
        return;
      default:
        return;
    }
  };
  visit(node, basePath);
  return refs;
};

/**
 * Enumerate the column refs in a `[col col col …]`-style arg (a vector
 * of symbol refs). Returns a `ColumnRefInArg[]` with each entry's path
 * keyed by `elemPath(basePath, i)` — matching `normalize.ts`'s spans
 * convention for vector elements.
 *
 * Verbs use this from their `referencedColumns` meta hook for column-list
 * args (select, distinct). The engine then runs `validateRefs` to surface
 * unknown-column-ref diagnostics and inspects the same list for per-cell
 * dispatch detection.
 */
export const colListRefs = (cols: unknown, basePath: string): readonly ColumnRefInArg[] => {
  if (!Array.isArray(cols)) {
    return [];
  }
  const refs: ColumnRefInArg[] = [];
  cols.forEach((c, i) => {
    if (isSymbolRef(c)) {
      refs.push({ name: c.$sym, path: elemPath(basePath, i) });
    }
  });
  return refs;
};

/**
 * Turn a verb's declared `referencedColumns` list into
 * `UnknownColumnRefError` diagnostics for any name that isn't on the
 * running input `schema`. Centralises the `(name, available, pos)`
 * triple and the `Array.from(schema.keys())` snapshot — every diagnostic
 * site builds the same shape, so it lives in one place.
 *
 * A `colLabels` ref (a `(col …)` expression-context tuple) is resolved
 * schema-aware via the shared role-based `pickColumnPath`: it is
 * satisfied iff *some* label names a schema column (the rest being an
 * ignored binding prefix / struct sub-path). Only when no label
 * resolves does it surface — same `UnknownColumnRefError`, so a typo'd
 * column gets the engine's "available columns" did-you-mean before any
 * I/O, while a valid `(col col subfield)` is not mis-flagged.
 *
 * Used as the default unknown-ref validator by `handleTransformForm`,
 * which derives refs from each verb's `referencedColumns` hook.
 */
export const validateRefs = (refs: readonly ColumnRefInArg[], schema: Schema, spans: Spans): readonly ExprError[] => {
  if (refs.length === 0) {
    return [];
  }
  const available = Array.from(schema.keys());
  const satisfied = (r: ColumnRefInArg): boolean =>
    r.colLabels === undefined ? schema.has(r.name) : pickColumnPath(r.colLabels, (n) => schema.has(n)) !== undefined;
  return refs.flatMap((r) =>
    satisfied(r) ? [] : [new UnknownColumnRefError(r.name, available, nodePos(spans, r.path))],
  );
};
